import axios from 'axios'

import { DetailResponse, ListResponse } from './resources/base'
import { ProjectDocument } from './resources/project'

const BASE_URL = process.env.REACT_APP_NEXTERA_API

export const listProjectDocuments = async (projectPublicId: string) => {
  const res = await axios.get<ListResponse<ProjectDocument>>(
    `${BASE_URL}/project/${projectPublicId}/documents`,
  )
  const response = res.data
  return response.data
}

export const fetchProjectDocument = async (
  projectPublicId: string,
  documentId: string | number,
) => {
  const res = await axios.get<DetailResponse<ProjectDocument>>(
    `${BASE_URL}/project/${projectPublicId}/document/${documentId}`,
  )
  const response = res.data
  return response.data
}

// TODO temporary delete function for project documents
export const deleteProjectDocument = async (
  projectPublicId: string,
  documentId: string | number,
) => {
  await axios.delete(
    `${BASE_URL}/project/${projectPublicId}/document/${documentId}`,
  )
}

export interface SafetyReportObservationSnapshot {
  id: number
  type_name: string
  severity: string
  unsafe: boolean
  subcontractor_name?: string
  notes?: string
  registered_on?: string
  image_src?: string
  floor_label?: string
  map_thumb_src?: string
  obs_href?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- report_context matches backend schema
export type SafetyReportReportContext = Record<string, any>

export interface SafetyReportPayload {
  report_date: string
  project_name: string
  /**
   * IDs of all observations the user selected for report scope (may include safe items).
   * The PDF photo appendix uses only `observations` (typically unsafe / highlight rows).
   */
  observation_ids: number[]
  /**
   * Serialized observation rows for the PDF: recommendation table + image pages.
   * Send only highlight-equivalent items (e.g. unsafe among selection) to limit payload size.
   */
  observations: SafetyReportObservationSnapshot[]
  heatmap_slugs: string[]
  observation_comments: Record<string, string>
  heatmap_comments: Record<string, string>
  manual_highlights: string[]
  summary_text?: string
  observation_stats?: { total: number }
  areas_captured?: number
  heatmap_images?: Array<{ slug: string; data_url?: string; src?: string }>
  report_context?: SafetyReportReportContext
}

export type SafetyReportJobCreateResponse = {
  job_id: string
  status: string
}

export type SafetyReportJobStatusResponse = {
  job_id: string
  status: 'pending' | 'running' | 'succeeded' | 'failed'
  error?: string
  document?: ProjectDocument
}

export interface ProgressReportColumnDef {
  jobTypeLabel: string
  columnId: string
  /** Right border after this column (tracker group boundary), like ProgressTable `column.border`. */
  dividerAfter?: boolean
}

export interface ProgressReportColumnGroup {
  trackerName: string
  columns: ProgressReportColumnDef[]
}

export interface ProgressReportCellPayload {
  percentText: string
  metricLines: string[]
  dateText?: string
  primaryPercentTone?: 'empty' | 'zero' | 'nonzero'
  dividerAfter?: boolean
  /** 0–100 for PDF vector bar */
  barPercent?: number | null
  showCompleteCheck?: boolean
  barTone?: 'empty' | 'in_progress' | 'complete'
}

export type ProgressReportRowVariant =
  | 'default'
  | 'overview'
  | 'zone'
  | 'zone_breakdown'

export interface ProgressReportTableRowPayload {
  label: string
  rowVariant?: ProgressReportRowVariant
  cells: ProgressReportCellPayload[]
}

/** Deep links for PDF cover: one row per physical floor, tracker names link to in-app MapViewer. */
export interface CoverFloorNavTrackerLink {
  label: string
  url: string
}

export interface CoverFloorNavItem {
  floor_label: string
  floor_code: string
  trackers: CoverFloorNavTrackerLink[]
}

export interface ProgressReportPayload {
  report_date: string
  project_name: string
  table: {
    column_groups: ProgressReportColumnGroup[]
    rows: ProgressReportTableRowPayload[]
  }
  report_context?: Record<string, unknown> & {
    cover_floor_nav?: CoverFloorNavItem[]
  }
}

export type ProgressReportJobCreateResponse = {
  job_id: string
  status: string
}

export type ProgressReportJobStatusResponse = SafetyReportJobStatusResponse

type ApiEnvelope<T> = { meta: unknown; data: T }

export const createSafetyReportJob = async (
  projectPublicId: string,
  payload: SafetyReportPayload,
): Promise<SafetyReportJobCreateResponse> => {
  const res = await axios.post<
    ApiEnvelope<SafetyReportJobCreateResponse> | SafetyReportJobCreateResponse
  >(`${BASE_URL}/project/${projectPublicId}/safety-reports/jobs`, payload)
  const body = res.data as ApiEnvelope<SafetyReportJobCreateResponse> &
    Partial<SafetyReportJobCreateResponse>
  if (body && typeof body === 'object' && 'data' in body && body.data) {
    return body.data
  }
  return body as SafetyReportJobCreateResponse
}

export const getSafetyReportJobStatus = async (
  projectPublicId: string,
  jobId: string,
): Promise<SafetyReportJobStatusResponse> => {
  const res = await axios.get<
    ApiEnvelope<SafetyReportJobStatusResponse> | SafetyReportJobStatusResponse
  >(`${BASE_URL}/project/${projectPublicId}/safety-reports/jobs/${jobId}`)
  const body = res.data as ApiEnvelope<SafetyReportJobStatusResponse> &
    Partial<SafetyReportJobStatusResponse>
  if (body && typeof body === 'object' && 'data' in body && body.data) {
    return body.data
  }
  return body as SafetyReportJobStatusResponse
}

export const createProgressReportJob = async (
  projectPublicId: string,
  payload: ProgressReportPayload,
): Promise<ProgressReportJobCreateResponse> => {
  const res = await axios.post<
    | ApiEnvelope<ProgressReportJobCreateResponse>
    | ProgressReportJobCreateResponse
  >(
    `${BASE_URL}/project/${projectPublicId}/progress-tracking-reports/jobs`,
    payload,
  )
  const body = res.data as ApiEnvelope<ProgressReportJobCreateResponse> &
    Partial<ProgressReportJobCreateResponse>
  if (body && typeof body === 'object' && 'data' in body && body.data) {
    return body.data
  }
  return body as ProgressReportJobCreateResponse
}

export const getProgressReportJobStatus = async (
  projectPublicId: string,
  jobId: string,
): Promise<ProgressReportJobStatusResponse> => {
  const res = await axios.get<
    | ApiEnvelope<ProgressReportJobStatusResponse>
    | ProgressReportJobStatusResponse
  >(
    `${BASE_URL}/project/${projectPublicId}/progress-tracking-reports/jobs/${jobId}`,
  )
  const body = res.data as ApiEnvelope<ProgressReportJobStatusResponse> &
    Partial<ProgressReportJobStatusResponse>
  if (body && typeof body === 'object' && 'data' in body && body.data) {
    return body.data
  }
  return body as ProgressReportJobStatusResponse
}

export type ReportGenerationJobListRow = {
  job_id: string
  project_public_id: string
  project_name: string
  kind: 'safety' | 'progress'
  status: 'pending' | 'running' | 'succeeded' | 'failed'
  report_for_date: string
  registered_on: string
  completed_at: string | null
  document_id: number | null
  error: string | null
}

export const listReportGenerationJobs = async (
  userPublicId: string,
): Promise<ReportGenerationJobListRow[]> => {
  const res = await axios.get<
    ApiEnvelope<ReportGenerationJobListRow[]> | ReportGenerationJobListRow[]
  >(`${BASE_URL}/user/${userPublicId}/report-generation-jobs`)
  const body = res.data as
    | ApiEnvelope<ReportGenerationJobListRow[]>
    | ReportGenerationJobListRow[]
  if (Array.isArray(body)) return body
  if (body && typeof body === 'object' && 'data' in body && body.data) {
    return body.data
  }
  return []
}
